import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, Download, Star, ShieldCheck } from "lucide-react";
import { RashiSection } from "@/pages/Kundli";
import { DoshaFeatures } from "@/pages/Dosha";
import VedicHowItWorks from "@/components/VedicHowItWorks";

const HERO = "https://images.unsplash.com/photo-1729335511904-9b8690184935?crop=entropy&cs=srgb&fm=jpg&q=85";
const RESULT_BG = "https://images.unsplash.com/photo-1652377755407-9b1cc4d48091?crop=entropy&cs=srgb&fm=jpg&q=85";
const GENDERS = ["Male", "Female", "Other"];
const LANGS = [
  { code: "", name: "English" }, { code: "hi", name: "हिन्दी" }, { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" }, { code: "bn", name: "বাংলা" }, { code: "mr", name: "मराठी" },
  { code: "gu", name: "ગુજરાતી" }, { code: "kn", name: "ಕನ್ನಡ" },
];

const RECEIVE = [
  "Detailed Rashi (moon sign) & Lagna analysis",
  "Complete Kundli birth chart & planetary houses",
  "Bhagyank (Destiny number) & Mulank (Root number)",
  "Personality, life path & lucky attributes",
  "Dosha identification — Mangal, Pitra, Kaal Sarp & Shani",
  "Traditional remedies, mantras & recommended pujas",
  "Downloadable PDF reports, also emailed to you",
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const scrollToResults = () =>
  document.querySelector("[data-testid=vedic-results-section]")?.scrollIntoView({ behavior: "smooth" });

function KundliReading({ r }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="result-kundli">
      <h3 className="font-serif text-xl font-semibold text-primary">Rashi & Kundli</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-center">
        {[["Moon Sign", r.moon_sign], ["Nakshatra", r.nakshatra], ["Lagna", r.lagna], ["Mulank", r.mulank], ["Bhagyank", r.bhagyank]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-secondary/60 p-3"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-serif font-semibold text-sm mt-1 leading-tight">{v}</p></div>
        ))}
      </div>
      <div>
        <h4 className="font-serif font-semibold mb-2">Planetary Positions</h4>
        <div className="space-y-1.5">{r.planetary_positions.map((p) => (
          <div key={p.planet} className="flex justify-between text-sm border-b border-border/60 pb-1.5"><span>{p.planet}</span><span className="text-muted-foreground">{p.sign} · House {p.house}</span></div>
        ))}</div>
      </div>
      <div><h4 className="font-serif font-semibold mb-1.5">Personality</h4><p className="text-sm text-muted-foreground leading-relaxed">{r.personality}</p></div>
      <div><h4 className="font-serif font-semibold mb-1.5">Life Path</h4><p className="text-sm text-muted-foreground leading-relaxed">{r.life_path}</p></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border">
        {[["Lucky No.", r.lucky.number], ["Colour", r.lucky.color], ["Day", r.lucky.day], ["Gemstone", r.lucky.gemstone]].map(([l, v]) => (
          <div key={l} className="text-center"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-semibold text-sm">{v}</p></div>
        ))}
      </div>
    </div>
  );
}

function DoshaReading({ r }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="result-dosha">
      <h3 className="font-serif text-xl font-semibold text-primary">Dosha & their Remedies</h3>
      <p className="text-sm text-muted-foreground">{r.summary}</p>
      {r.doshas.map((d) => (
        <div key={d.name} className="rounded-xl border border-border p-4">
          <h4 className="font-serif font-semibold text-primary">{d.name}</h4>
          <p className="text-sm text-muted-foreground mt-1">{d.effect}</p>
          <p className="text-xs font-semibold uppercase tracking-wide text-accent mt-3 mb-1.5">Remedies</p>
          <ul className="space-y-1">{d.remedies.map((rem) => <li key={rem} className="flex gap-2 text-sm"><span className="text-accent">•</span> {rem}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

export default function KundliDosha() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ name: "", gender: "Male", dob: "", tob: "", pob: "" });
  const [sel, setSel] = useState({ kundli: true, dosha: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pricing, setPricing] = useState({ kundli: 0, dosha: 199, match: 251 });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [cities, setCities] = useState([]);
  const [lang, setLang] = useState("");

  React.useEffect(() => {
    api.get("/vedic/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/geo/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const base = (sel.kundli ? pricing.kundli : 0) + (sel.dosha ? pricing.dosha : 0);
  const bundle = (sel.kundli && sel.dosha) ? Math.min(pricing.bundle_discount || 0, base) : 0;
  const afterBundle = Math.max(0, base - bundle);
  const total = Math.max(0, afterBundle - discount);
  const detailsValid = form.name && form.dob && form.tob && form.pob;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    if (!user) { login(); return; }
    try {
      const r = await api.post("/vedic/coupon/validate", { code: coupon, amount: afterBundle });
      setDiscount(r.data.discount);
      setCouponMsg(`Coupon applied — you save ₹${r.data.discount}`);
    } catch (e) { setDiscount(0); setCouponMsg(e?.response?.data?.detail || "Invalid coupon"); }
  };

  const finish = (data) => {
    setResult(data);
    setLoading(false);
    toast.success("Your reports are ready");
    setTimeout(scrollToResults, 100);
  };

  const startBooking = async () => {
    if (!user) { login(); return; }
    if (!detailsValid) { toast.error("Please fill all birth details"); return; }
    if (!sel.kundli && !sel.dosha) { toast.error("Select at least one report"); return; }
    setLoading(true);
    try {
      const cc = cities.find((c) => c.name.toLowerCase() === (form.pob || "").trim().toLowerCase());
      const res = await api.post("/vedic/report", { ...form, coordinates: cc?.coord || null, lang: lang || null, include_kundli: sel.kundli, include_dosha: sel.dosha, coupon: discount > 0 ? coupon : null });
      if (res.data.paid) return finish(res.data);
      if (res.data.live) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: res.data.key_id, order_id: res.data.order_id, amount: res.data.amount, currency: res.data.currency,
          name: "Pavitra Mantra", description: "Vedic Report",
          prefill: { name: form.name, email: user?.email },
          theme: { color: "#C58208" },
          modal: { ondismiss: () => setLoading(false) },
          handler: async (resp) => {
            try {
              const v = await api.post("/vedic/report/verify", {
                report_id: res.data.report_id, razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id, razorpay_signature: resp.razorpay_signature,
              });
              finish(v.data);
            } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); setLoading(false); }
          },
        });
        rzp.open();
      } else {
        const v = await api.post("/vedic/report/verify", { report_id: res.data.report_id });
        finish(v.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
      setLoading(false);
    }
  };

  const Option = ({ id, title, desc, price }) => (
    <button
      type="button" onClick={() => setSel({ ...sel, [id]: !sel[id] })}
      data-testid={`report-option-${id}`}
      className={`w-full text-left flex items-start gap-3 rounded-xl border p-4 transition-all ${sel[id] ? "border-accent bg-accent/5" : "border-border hover:border-accent/60"}`}>
      <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 grid place-items-center ${sel[id] ? "bg-accent border-accent" : "border-muted-foreground/40"}`}>
        {sel[id] && <Check className="h-3 w-3 text-white" />}
      </span>
      <span className="flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="font-serif font-semibold">{title}</span>
          <span className={`text-sm font-semibold ${price === 0 ? "text-emerald-600" : "text-primary"}`}>{price === 0 ? "Nil" : `₹${price}`}</span>
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5">{desc}</span>
      </span>
    </button>
  );

  return (
    <div data-testid="kundli-dosha-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="h-full w-full object-cover opacity-90" /><div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-20">
          <p className="font-devanagari text-accent text-lg mb-2">॥ कुण्डली एवं दोष ॥</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold max-w-2xl leading-tight">Rashi, Kundli & Dosha Reading</h1>
          <p className="mt-4 text-muted-foreground max-w-xl text-lg">Discover your birth chart, planetary positions, numerology and doshas — with traditional remedies, delivered as downloadable PDF reports.</p>
        </div>
      </section>

      {/* Two-column: birth details + what you receive */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-serif text-2xl font-semibold mb-4">Enter birth details</h2>
          <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <div><Label className="mb-1.5 block">Name of Devotee *</Label><Input data-testid="vedic-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
            <div>
              <Label className="mb-1.5 block">Gender</Label>
              <div className="flex gap-2">{GENDERS.map((g) => <button key={g} onClick={() => setForm({ ...form, gender: g })} data-testid={`gender-${g}`} className={`px-4 py-2 rounded-full text-sm border ${form.gender === g ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>{g}</button>)}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="mb-1.5 block">Date of Birth *</Label><Input type="date" data-testid="vedic-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="rounded-xl" /></div>
              <div><Label className="mb-1.5 block">Time of Birth *</Label><Input type="time" data-testid="vedic-tob" value={form.tob} onChange={(e) => setForm({ ...form, tob: e.target.value })} className="rounded-xl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Place of Birth *</Label>
                <Input list="pm-cities" data-testid="vedic-pob" value={form.pob} onChange={(e) => setForm({ ...form, pob: e.target.value })} className="rounded-xl" placeholder="Start typing a city…" />
                <datalist id="pm-cities">{cities.map((c) => <option key={c.name} value={c.name}>{c.state}</option>)}</datalist>
              </div>
              <div>
                <Label className="mb-1.5 block">Report language</Label>
                <select data-testid="vedic-lang" value={lang} onChange={(e) => setLang(e.target.value)} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
                  {LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-2xl font-semibold mb-4">When you book, you receive</h2>
          <div className="rounded-2xl border border-border bg-secondary/40 p-6 h-[calc(100%-3rem)]">
            <ul className="space-y-3 text-sm text-muted-foreground">
              {RECEIVE.map((r) => <li key={r} className="flex gap-2.5"><Sparkles className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {r}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Selection + pay */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto pb-4" data-testid="report-selection-section">
        <div className="rounded-2xl border border-accent/40 bg-card p-6 shadow-sm">
          <h2 className="font-serif text-2xl font-semibold mb-1">Choose your reports</h2>
          <p className="text-sm text-muted-foreground mb-5">Select the readings you would like. Rashi & Kundli is free; Dosha & Remedies is a paid add-on.</p>
          <div className="space-y-3">
            <Option id="kundli" title="Rashi & Kundli" desc="Birth chart, planetary positions, numerology & life insights" price={pricing.kundli} />
            <Option id="dosha" title="Dosha & Remedies" desc="Mangal, Pitra, Kaal Sarp & Shani doshas with traditional remedies" price={pricing.dosha} />
          </div>
          <div className="mt-4 flex gap-2">
            <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="rounded-full" />
            <Button type="button" variant="outline" onClick={applyCoupon} data-testid="apply-coupon-btn" className="rounded-full border-accent text-primary shrink-0">Apply</Button>
          </div>
          {couponMsg && <p data-testid="coupon-msg" className={`text-xs mt-1.5 ${discount > 0 ? "text-emerald-600" : "text-destructive"}`}>{couponMsg}</p>}
          <div className="mt-5 pt-4 border-t border-border space-y-1.5">
            <div className="flex items-center justify-between text-sm text-muted-foreground"><span>Subtotal</span><span>{base === 0 ? "Nil" : `₹${base}`}</span></div>
            {bundle > 0 && <div className="flex items-center justify-between text-sm text-emerald-600"><span>Bundle discount</span><span>− ₹{bundle}</span></div>}
            {discount > 0 && <div className="flex items-center justify-between text-sm text-emerald-600"><span>Coupon discount</span><span>− ₹{discount}</span></div>}
            <div className="flex items-center justify-between">
              <span className="font-serif text-lg font-semibold">Grand Total</span>
              <span data-testid="grand-total" className="font-serif text-xl font-bold text-primary">{total === 0 ? "Nil" : `₹${total}`}</span>
            </div>
          </div>
          <Button onClick={startBooking} disabled={loading || (!sel.kundli && !sel.dosha)} data-testid="confirm-pay-btn" className="w-full mt-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : total === 0 ? <>Confirm & Generate (Free) <Sparkles className="ml-2 h-4 w-4" /></> : <>Confirm & Pay ₹{total} <ShieldCheck className="ml-2 h-4 w-4" /></>}
          </Button>
          {!user && <p className="text-xs text-center text-muted-foreground mt-2">You'll be asked to sign in with Google.</p>}
        </div>
      </section>

      {/* Results — transparent background image */}
      <section className="relative overflow-hidden mt-8" data-testid="vedic-results-section">
        <div className="absolute inset-0"><img src={RESULT_BG} alt="" className="h-full w-full object-cover opacity-10" /><div className="absolute inset-0 bg-background/70" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto py-14 lg:py-16">
          <h2 className="font-serif text-2xl font-semibold text-center mb-6">Your personalised reading</h2>
          {result ? (
            <div className="space-y-6 animate-float-up">
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-700 dark:text-emerald-400 text-center">✓ Payment confirmed for {form.name}. Your PDF report(s) have been emailed to you.</div>
              <div className="flex flex-wrap gap-3 justify-center">
                {result.downloads?.combined && <a href={result.downloads.combined} target="_blank" rel="noreferrer" data-testid="download-combined-btn"><Button className="rounded-full bg-primary text-primary-foreground"><Download className="h-4 w-4 mr-2" /> Complete Report PDF</Button></a>}
                {result.downloads?.kundli && <a href={result.downloads.kundli} target="_blank" rel="noreferrer" data-testid="download-kundli-btn"><Button variant="outline" className="rounded-full border-accent text-primary"><Download className="h-4 w-4 mr-2" /> Rashi & Kundli PDF</Button></a>}
                {result.downloads?.dosha && <a href={result.downloads.dosha} target="_blank" rel="noreferrer" data-testid="download-dosha-btn"><Button variant="outline" className="rounded-full border-accent text-primary"><Download className="h-4 w-4 mr-2" /> Dosha & Remedies PDF</Button></a>}
              </div>
              {result.readings?.kundli && <KundliReading r={result.readings.kundli} />}
              {result.readings?.dosha && <DoshaReading r={result.readings.dosha} />}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-card/70 p-12 text-center">
              <Star className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">Your personalised reading will appear here after booking.</p>
            </div>
          )}
        </div>
      </section>

      <RashiSection />
      <DoshaFeatures />
      <VedicHowItWorks
        subtitle="Get your Rashi, Kundli and Dosha reading in four simple steps."
        steps={[
          { title: "Enter birth details", desc: "Share your name, date, time and place of birth." },
          { title: "Choose reports", desc: "Pick Rashi & Kundli (free) and/or Dosha & Remedies (₹199)." },
          { title: "Confirm & pay", desc: "Free reports generate instantly; paid ones via secure Razorpay checkout." },
          { title: "Get your PDFs", desc: "Read on-screen and receive downloadable PDFs by email." },
        ]}
      />
    </div>
  );
}
